<template>
    <DCard
        :title="__('products.partner_api_products.edit.tenant_label')"
        :main-title-has-slot="true"
        padding
    >
        <template #titleHasSlot>
            <strong>{{ resellerTenant }}</strong>
        </template>
        <DAlert
            color="info"
            class="d-flex align-items-center"
        >
            <DIcon
                name="information-outline"
                class="info-icon"
                :size="20"
            />
            {{ __('products.partner_api_products.edit.info_banner') }}
        </DAlert>

        <DTable
            :items="rows"
            :fields="[
                {key: 'reseller_product_id', label: __('products.partner_api_products.edit.reseller_product_id'), sorter: false, filter: false},
                {key: 'reseller_product_name', label: __('products.partner_api_products.edit.reseller_product_name'), sorter: false, filter: false},
                {key: 'reseller_product_code', label: __('products.partner_api_products.edit.reseller_product_code'), sorter: false, filter: false},
                {key: 'price', label: __('products.partner_api_products.edit.price'), sorter: false, filter: false},
                {key: 'billing_period', label: __('products.partner_api_products.edit.billing_period'), sorter: false, filter: false},
                {key: 'billing_type', label: __('products.partner_api_products.edit.billing_type'), sorter: false, filter: false},
                {key: 'status', label: __('products.partner_api_products.edit.status'), sorter: false, filter: false},
                {key: 'action', label: __('products.partner_api_products.edit.action'), sorter: false, filter: false},
            ]"
            hover
        >
            <template #reseller_product_code="{item}">
                <td>
                    {{ item.reseller_product_code ?? '' }}
                </td>
            </template>

            <template #price="{item}">
                <td>
                    <DInputGroup>
                        <DInputGroupText
                            v-if="currency"
                            :value="currency"
                        />
                        <DInput
                            :id="`price_${item.id}`"
                            v-model="item.price"
                            :disabled="item.status === 'inactive' || saving"
                            :invalid-feedback="rowErrors[item.id]?.price"
                            maxlength="13"
                        />
                    </DInputGroup>
                </td>
            </template>

            <template #billing_period="{item}">
                <td>
                    <DSelect
                        :id="`billing_period_${item.id}`"
                        v-model="item.billing_period"
                        :options="billingCycles"
                        :disabled="item.status === 'inactive' || saving"
                        :invalid-feedback="rowErrors[item.id]?.billing_period"
                    />
                </td>
            </template>

            <template #billing_type="{item}">
                <td>
                    <DSelect
                        :id="`billing_type_${item.id}`"
                        v-model="item.billing_type"
                        :options="billingTypes"
                        :disabled="item.status === 'inactive' || saving"
                        :invalid-feedback="rowErrors[item.id]?.billing_type"
                    />
                </td>
            </template>

            <template #status="{item}">
                <td>
                    <DBadge :color="getStatus(item.status).color">
                        {{ getStatus(item.status).label }}
                    </DBadge>
                </td>
            </template>

            <template #action="{item}">
                <td>
                    <DButton
                        :id="`save_${item.id}`"
                        mini
                        color="success"
                        icon="content-save"
                        :disabled="item.status === 'inactive' || saving"
                        @click="saveRow(item)"
                    />
                </td>
            </template>
        </DTable>

        <DPagination
            v-if="products.last_page >= 1"
            :active-page="products.current_page"
            :pages="products.last_page"
            align="center"
            @update:activePage="changePage"
        />
    </DCard>
</template>

<script setup>
import { router } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'
import { ref, watch } from 'vue'

const props = defineProps({
    contract: {
        type: Object,
        default: () => { return {} }
    },
    resellerTenant: {
        type: String,
        default: ''
    },
    products: {
        type: Object,
        default: () => { return {} }
    },
    currency: {
        type: String,
        default: null
    },
    billingTypes: {
        type: Array,
        default: () => { return [] }
    },
    billingCycles: {
        type: Array,
        default: () => { return [] }
    }
})

const rows = ref((props.products.data ?? []).map(item => ({ ...item })))
const rowErrors = ref({})
const saving = ref(false)

watch(() => props.products.data, (newData) => {
    (newData ?? []).forEach(item => {
        const row = rows.value.find(r => r.id === item.id)

        if (row) {
            row.status = item.status
        }
    })
})

const statuses = {
    configured: {
        color: 'success',
        label: __('products.partner_api_products.edit.status_configured'),
    },
    inactive: {
        color: 'danger',
        label: __('products.partner_api_products.edit.status_inactive'),
    },
    missing: {
        color: 'warning',
        label: __('products.partner_api_products.edit.status_missing'),
    }
}

function getStatus (status) {
    return statuses[status] ?? statuses.missing
}

function saveRow (item) {
    rowErrors.value[item.id] = {}
    saving.value = true

    router.put(route('products.partner-api-products.update', { partnerApiProduct: item.id }), {
        price: item.price,
        billing_period: item.billing_period,
        billing_type: item.billing_type,
    }, {
        onError: (errors) => {
            rowErrors.value[item.id] = errors
        },
        onFinish: () => {
            saving.value = false
        }
    })
}

function changePage (page) {
    const queryParameters = {}

    if (page > 1) {
        queryParameters.page = page
    }

    router.get(route('products.partner-api-products.edit', { contract: props.contract.id }), queryParameters)
}
</script>

<style lang="scss" scoped>
.info-icon {
    padding-right: 5px;
    margin-left: -5px;
}
</style>
